import { useNavigate } from "react-router-dom";
import { ENGINE_LABELS, type Engine } from "@velivo/shared";
import { Icon } from "../Icon";

/**
 * FR-DASH-04 — Strategic Narrative Drift & Misinformation Radar.
 *
 * Severity-ranked cards, each carrying the four things the FRD requires on the
 * card itself rather than behind a click: the misstatement, the Ground Truth
 * reality, the affected engine, and the root cause. A triage list that makes
 * you open every item to find out whether it matters isn't a triage list.
 */

export interface RadarFlag {
  flagId: string;
  pillarId: string;
  engine: Engine;
  driftType: string;
  severity: "aligned" | "erosion" | "critical";
  rootCause: string;
  rootCauseRationale: string | null;
  description: string;
  groundTruthStatement: string | null;
  impactScore: number;
}

const DRIFT_LABELS: Record<string, string> = {
  omission: "Omission",
  distortion: "Distortion",
  outdated_fact: "Outdated fact",
  hostile_context: "Hostile context",
};

const ROOT_CAUSE_LABELS: Record<string, string> = {
  knowledge_gap: "Knowledge gap",
  contamination: "Contamination",
  undetermined: "Undetermined",
};

const RANK: Record<string, number> = { critical: 0, erosion: 1, aligned: 2 };

export function DriftRadar({
  flags,
  pillarName,
  limit = 6,
}: {
  flags: RadarFlag[] | null;
  pillarName: (id: string) => string;
  limit?: number;
}) {
  const navigate = useNavigate();

  if (!flags) {
    return (
      <div className="skeleton-stack">
        {[0, 1].map((i) => (
          <div className="skeleton" key={i} style={{ height: 120 }} />
        ))}
      </div>
    );
  }

  if (flags.length === 0) {
    return (
      <p className="note-empty">
        Nothing has been flagged in this period. When a scored response diverges
        from your approved Ground Truth, it appears here worst-first.
      </p>
    );
  }

  const ranked = [...flags]
    .sort(
      (a, b) =>
        (RANK[a.severity] ?? 9) - (RANK[b.severity] ?? 9) ||
        b.impactScore - a.impactScore,
    )
    .slice(0, limit);

  return (
    <>
      <ul className="radarcards">
        {ranked.map((flag) => (
          <li
            className={`radarcard radarcard--${flag.severity}`}
            key={flag.flagId}
          >
            <div className="radarcard__head">
              <span className={`badge badge--${flag.severity}`}>
                {flag.severity}
              </span>
              <span className="badge badge--neutral">
                {DRIFT_LABELS[flag.driftType] ?? flag.driftType}
              </span>
              <span className={`badge badge--cause-${flag.rootCause}`}>
                {ROOT_CAUSE_LABELS[flag.rootCause] ?? flag.rootCause}
              </span>
              <span className="radarcard__where">
                {pillarName(flag.pillarId)} ·{" "}
                {ENGINE_LABELS[flag.engine] ?? flag.engine}
              </span>
              <span className="radarcard__impact" title="Impact score">
                {flag.impactScore}
              </span>
            </div>

            <p className="evidence__quote evidence__quote--drift">
              {flag.description}
            </p>
            {flag.groundTruthStatement ? (
              <p className="evidence__quote evidence__quote--truth">
                {flag.groundTruthStatement}
              </p>
            ) : null}
            {flag.rootCauseRationale ? (
              <p className="radarcard__why">{flag.rootCauseRationale}</p>
            ) : null}

            <div className="radarcard__foot">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => navigate(`/drift?flagId=${flag.flagId}`)}
              >
                See the evidence <Icon name="arrowRight" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      {flags.length > ranked.length ? (
        <p className="muted" style={{ marginTop: "var(--space-3)" }}>
          Showing the {ranked.length} highest-severity of {flags.length} flags
          in this period.
        </p>
      ) : null}
    </>
  );
}
